import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useSite } from "@/contexts/SiteContext";
import Reveal from "@/components/Reveal";
import AnimatedCounter from "@/components/AnimatedCounter";
import { Quote, MapPin } from "lucide-react";
import useSEO from "@/hooks/useSEO";

export default function About() {
  const { company } = useSite();
  useSEO({
    title: "La Société",
    description: "Découvrez AMAR SAHRAOUI Promotion Immobilière : notre histoire, notre vision, nos valeurs et nos chiffres clés. Promoteur immobilier haut de gamme implanté à Oran, Algérie.",
    path: "/societe",
  });
  const [, setTestimonials] = useState([]);

  useEffect(() => {
    api.get("/public/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[55vh] min-h-[420px] flex items-end overflow-hidden bg-atelier-deep">
        <img
          src="https://images.unsplash.com/photo-1628012209120-d9db7abf7eab?auto=format&fit=crop&w=2200&q=80"
          alt="Société"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-atelier-deep via-atelier-deep/60 to-transparent" />
        <div className="absolute inset-0 blueprint-grid opacity-20" aria-hidden="true" />
        <div className="relative px-5 lg:px-10 pb-14 w-full">
          <div className="label-tech text-atelier-ink mb-4 flex items-center gap-3">
            <span className="w-10 h-px bg-atelier-ink" /> La Société
          </div>
          <h1 className="font-serif text-5xl lg:text-7xl tracking-tight leading-[1.02] text-atelier-text">
            Bâtisseurs d'un <span className="italic text-atelier-ink">art de vivre.</span>
          </h1>
        </div>
      </section>

      {/* Présentation */}
      <section className="py-20 lg:py-28 bg-atelier">
        <div className="px-5 lg:px-10 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Reveal>
              <div className="label-tech text-atelier-ink mb-4">— Présentation</div>
              <h2 className="font-serif text-4xl lg:text-5xl leading-tight tracking-tight text-atelier-text">{company?.name}</h2>
            </Reveal>
          </div>
          <div className="md:col-span-7">
            <Reveal delay={100}>
              <p className="font-serif italic text-xl text-atelier-ink mb-6">{company?.slogan}</p>
              <p className="text-base text-atelier-textDim leading-relaxed whitespace-pre-line font-serif">{company?.long_about}</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Vision / Mission / Histoire */}
      <section className="py-20 bg-atelier-deep border-y atelier-border-soft">
        <div className="px-5 lg:px-10 grid md:grid-cols-3 gap-px atelier-border bg-atelier-line">
          <Reveal>
            <div className="bg-atelier-deep p-10">
              <div className="label-tech text-atelier-ink mb-4">— Vision</div>
              <p className="font-serif text-xl leading-snug text-atelier-text">{company?.vision}</p>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <div className="bg-atelier-deep p-10">
              <div className="label-tech text-atelier-ink mb-4">— Mission</div>
              <p className="font-serif text-xl leading-snug text-atelier-text">{company?.mission}</p>
            </div>
          </Reveal>
          <Reveal delay={200}>
            <div className="bg-atelier-deep p-10">
              <div className="label-tech text-atelier-ink mb-4">— Histoire</div>
              <p className="font-serif text-xl leading-snug text-atelier-text">{company?.history}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Valeurs */}
      {company?.values?.length > 0 && (
        <section className="py-20 lg:py-28 bg-atelier">
          <div className="px-5 lg:px-10">
            <div className="mb-12">
              <div className="label-tech text-atelier-ink mb-4">— Nos valeurs</div>
              <h2 className="font-serif text-4xl lg:text-5xl text-atelier-text">Ce qui nous <span className="italic text-atelier-ink">anime</span> au quotidien.</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 atelier-border">
              {company.values.map((v, i) => (
                <div key={i} className="bg-atelier-surface/30 p-8 border-r border-b atelier-border-soft scan-card">
                  <div className="text-atelier-ink font-serif text-5xl mb-4 leading-none">{String(i + 1).padStart(2, "0")}</div>
                  <h3 className="label-tech text-atelier-text mb-3 text-sm">{v.title}</h3>
                  <p className="text-sm text-atelier-textDim leading-relaxed font-serif">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Mot du dirigeant */}
      {company?.director_word && (
        <section className="py-20 lg:py-28 bg-atelier-deep relative overflow-hidden">
          <div className="absolute inset-0 blueprint-grid opacity-15" aria-hidden="true" />
          <div className="max-w-4xl mx-auto px-5 lg:px-10 text-center relative z-10">
            <Quote className="w-12 h-12 text-atelier-ink mx-auto mb-8" strokeWidth={1} />
            <p className="font-serif italic text-2xl lg:text-3xl leading-snug mb-10 text-atelier-text">"{company.director_word}"</p>
            <div className="label-tech text-atelier-ink">— {company.director_name}, Direction Générale</div>
          </div>
        </section>
      )}

      {/* Chiffres clés */}
      {company?.key_figures?.length > 0 && (
        <section className="py-20 bg-atelier">
          <div className="px-5 lg:px-10">
            <div className="mb-12">
              <div className="label-tech text-atelier-ink mb-4">— Chiffres clés</div>
              <h2 className="font-serif text-4xl lg:text-5xl text-atelier-text">Une expertise <span className="italic text-atelier-ink">éprouvée.</span></h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 atelier-border">
              {company.key_figures.map((kf, i) => (
                <div key={i} className="bg-atelier-surface/30 p-10 border-r border-b atelier-border-soft">
                  <div className="font-serif text-5xl lg:text-7xl text-atelier-ink leading-none">
                    <AnimatedCounter value={kf.value} />
                  </div>
                  <div className="label-tech text-atelier-textDim mt-3">{kf.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Zones */}
      {company?.zones?.length > 0 && (
        <section className="py-20 bg-atelier-deep border-y atelier-border-soft">
          <div className="px-5 lg:px-10">
            <div className="label-tech text-atelier-ink mb-4">— Zones d'intervention</div>
            <h2 className="font-serif text-3xl lg:text-5xl mb-10 text-atelier-text">À Oran et bien <span className="italic text-atelier-ink">au-delà.</span></h2>
            <div className="flex flex-wrap gap-3">
              {company.zones.map((z) => (
                <span key={z} className="flex items-center gap-2 px-4 py-2 atelier-border text-sm text-atelier-text">
                  <MapPin className="w-3 h-3 text-atelier-ink" strokeWidth={1.4} /> {z}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
